import { cn, Icon, Tooltip, Typography } from "@kyan/ui";
import type { ReactNode } from "react";

import { formatValue } from "#/utils/valueFormatters";

import { LoadOrRender } from "../LoadOrRender/LoadOrRender";

export type InfoLineVariant = "change" | "amount";

export interface InfoLineData {
  label: string;
  change?: {
    from: number;
    to: number;
    template?: string;
  };
  amount?: number;
  unit?: string;
  template?: string;
}

export interface InfoLineProps {
  isGreek?: boolean;
  data: InfoLineData;
  isLoading?: boolean;
  tooltip?: ReactNode;
  className?: string;
  leftLabelClassName?: string;
  rightLabelClassName?: string;
}

export const InfoLine: React.FC<InfoLineProps> = ({
  isLoading,
  data,
  tooltip,
  className,
  isGreek,
  leftLabelClassName,
  rightLabelClassName,
}) => {
  const { change, label, amount, unit, template } = data;
  const isChangeVariant = Boolean(change);

  return (
    <div
      className={cn("flex h-4 w-full items-center justify-between", className)}
    >
      {Boolean(tooltip) && (
        <Tooltip avoidCollisions content={tooltip}>
          <button className="cursor-help text-xs underline">{label}</button>
        </Tooltip>
      )}
      {!tooltip && <Typography xs>{label}</Typography>}

      <LoadOrRender isLoading={isLoading}>
        <div className="flex items-center gap-2 text-xs">
          {isChangeVariant && change ? (
            <>
              <Typography xs variant="primary" className={leftLabelClassName}>
                {formatValue(change.from, {
                  template: change.template,
                  trailingZeroDisplay: "stripIfInteger",
                })}
              </Typography>
              <Icon size="xs" name="arrow-up" className="rotate-90" />

              <div className={cn("flex", { "w-12 justify-end": isGreek })}>
                <Typography
                  xs
                  variant="primary"
                  className={rightLabelClassName}
                >
                  {formatValue(change.to, {
                    template: change.template,
                    trailingZeroDisplay: "stripIfInteger",
                  })}
                </Typography>
              </div>
            </>
          ) : null}
          {!isChangeVariant && amount !== undefined ? (
            <>
              <Typography xs variant="primary" className={leftLabelClassName}>
                {formatValue(amount ?? 0, {
                  template,
                  trailingZeroDisplay: "stripIfInteger",
                })}
              </Typography>
              {unit && <Typography xs>{unit}</Typography>}
            </>
          ) : null}
        </div>
      </LoadOrRender>
    </div>
  );
};
